import { atom, selector, selectorFamily } from 'recoil'

import getIntl from 'Helpers/intl'

import {
  CONTENT_SOURCE_UPDATE_STATUS_OK,
  CONTENT_SOURCE_UPDATE_STATUS_KO,
  IMG_POS_TOP,
  IMG_POS_LEFT,
  IMG_POS_RIGHT,
  IMG_SPACE_EQUAL,
  IMG_SPACE_SMALL
} from '../constants'

export const modalBeeContentDialogState = atom({
  key: 'modalBeeContentDialogState',
  default: {
    //
    resolveCallback: undefined,
    rejectCallback: undefined,
    loading: false,
    loaded: false,
    sources: [],
    step: 1,
    //
    error: undefined,
    severity: undefined,
    //
    sourceId: undefined,
    sourceStatus: undefined,
    sourceContents: 0,
    contentsPerRow: undefined,
    imgPos: IMG_POS_TOP,
    imgSpace: IMG_SPACE_EQUAL,
    repeat: false,
    repeatTimes: 2,
    selectedContents: {} //  position:  {contentKey,title}
  }
})

export const sourcesFeedbackState = selector({
  key: 'modalBeeContentDialogSourcesFeedbackState',
  get: ({ get }) => {
    const { sourceId, sourceStatus, sourceContents } = get(
      modalBeeContentDialogState
    )

    return sourceId && sourceContents === 0
      ? {
          feedback: getIntl().formatMessage({
            id: 'Load.BeeContentDialog.NoContentsInSourceTitle'
          }),
          feedbackVariant: 'danger'
        }
      : sourceId && sourceStatus === CONTENT_SOURCE_UPDATE_STATUS_KO
      ? {
          feedback: getIntl().formatMessage({
            id: 'Load.ContentsSouces.UpdateSourcesFail'
          }),
          feedbackVariant: 'grey'
        }
      : {}
  }
})

export const emptyStateState = selector({
  key: 'modalBeeContentDialogEmptyStateState',
  get: ({ get }) => {
    const { loaded, sources } = get(modalBeeContentDialogState)
    return loaded && sources.length === 0
  }
})

export const validStep1State = selector({
  key: 'modalBeeContentDialogValidStep1State',
  get: ({ get }) => {
    const { sourceId, sourceContents, contentsPerRow } = get(
      modalBeeContentDialogState
    )
    return sourceId && sourceContents && contentsPerRow
  }
})

export const templates = {
  1: {
    id: 1,
    contentsPerRow: 1,
    imgPos: IMG_POS_TOP,
    imgSpace: IMG_SPACE_EQUAL
  },
  2: {
    id: 2,
    contentsPerRow: 1,
    imgPos: IMG_POS_LEFT,
    imgSpace: IMG_SPACE_EQUAL
  },
  3: {
    id: 3,
    contentsPerRow: 1,
    imgPos: IMG_POS_RIGHT,
    imgSpace: IMG_SPACE_EQUAL
  },
  4: {
    id: 4,
    contentsPerRow: 1,
    imgPos: IMG_POS_LEFT,
    imgSpace: IMG_SPACE_SMALL
  },
  5: {
    id: 5,
    contentsPerRow: 1,
    imgPos: IMG_POS_RIGHT,
    imgSpace: IMG_SPACE_SMALL
  },
  6: { id: 6, contentsPerRow: 2, imgPos: undefined, imgSpace: undefined },
  7: { id: 7, contentsPerRow: 3, imgPos: undefined, imgSpace: undefined },
  8: { id: 8, contentsPerRow: 4, imgPos: undefined, imgSpace: undefined }
}

export const modalBeeContentDialogSearchState = atom({
  key: 'modalBeeContentDialogSearchState',
  default: {
    searchBy: 'Title',
    searchText: '',
    orderBy: 'ContentDate',
    orderDescending: true
  }
})

export const modalBeeContentDialogContentsState = atom({
  key: 'modalBeeContentDialogContentsState',
  default: {
    fetching: false,
    error: false,
    severity: undefined
  }
})

export const modalBeeContentDialogResultsState = atom({
  key: 'modalBeeContentDialogResultsState',
  default: {
    items: [],
    pageNumber: 1,
    pageSize: 5,
    totalItems: 0
  }
})

export const selectedContentsEntriesState = selector({
  key: 'modalBeeContentDialogSelectedContentsEntriesState',
  get: ({ get }) => {
    const { selectedContents } = get(modalBeeContentDialogState)
    return Object.entries(selectedContents)
  }
})

export const contentPositionState = selectorFamily({
  key: 'modalBeeContentDialogContentPositionState',
  get: contentKey => ({ get }) => {
    //  const { selectedContents } = get(modalBeeContentDialogState)
    //  const selectedContentsEntries =  Object.entries(selectedContents)
    const selectedContentsEntries = get(selectedContentsEntriesState)

    const itemConfig = selectedContentsEntries.find(
      ([_, content]) => content && content.contentKey === contentKey
    )
    const position = itemConfig ? itemConfig[0] : undefined
    return position
  },
  set: contentKey => ({ get, set }, newPosition) => {
    const currentPosition = get(contentPositionState(contentKey))
    const { items } = get(modalBeeContentDialogResultsState)
    const content = items.find(({ contentKey: ck }) => ck === contentKey)

    set(modalBeeContentDialogState, prev => ({
      ...prev,
      selectedContents: {
        ...prev.selectedContents,
        ...(currentPosition
          ? {
              [currentPosition]: null
            }
          : {}),
        ...(newPosition > 0 ? { [newPosition]: content } : {})
      }
    }))
  }
})

export const contentPositionOptionsState = selectorFamily({
  key: 'modalBeeContentDialogContentPositionOptionsState',
  get: contentKey => ({ get }) => {
    const intl = getIntl()
    const { selectedContents } = get(modalBeeContentDialogState)
    const entries = get(selectedContentsEntriesState)

    const positionOptions = entries.map(([position, content]) => ({
      label: `${intl.formatMessage({
        id: 'Load.BeeContentDialog.Position'
      })} ${position}`,
      value: position,
      disabled:
        content &&
        content.contentKey !== contentKey &&
        !!selectedContents[position]
    }))

    return positionOptions
  }
})

export const contentIndicatorState = selector({
  key: 'modalBeeContentDialogContentIndicatorState',
  get: ({ get }) => {
    const entries = get(selectedContentsEntriesState)
    const assigned = entries.filter(([_, content]) => !!content)

    return [assigned.length, entries.length]
  }
})

export const validStep2State = selector({
  key: 'modalBeeContentDialogValidStep2State',
  get: ({ get }) => {
    const validStep1 = get(validStep1State)
    const [count, total] = get(contentIndicatorState)

    return validStep1 && count === total
  }
})
